import { useMutation, useQuery } from "@tanstack/react-query";
import { rightsApiService } from "modules/Library/Role/api/rights.api";

interface ApiResponse<T = any> {
  data: T;
  message: string;
  statuscode: number;
}

// API data shapes (based on the formats you shared)
export type RightsMatrixAction = { actionid: number; actionname: string };
export type RightsMatrixModule = {
  formid: number;
  formname: string;
  actions: RightsMatrixAction[];
};

export type AssignedRight = { formid: number; actionid: number };

export type UpdateRightsPayload = {
  roleid?: number;
  rights: AssignedRight[]; // [{ formid, actionid }]
  updatereason: string;
};

export type InsertRightsPayload = {
  roleid?: number;
  rights: AssignedRight[];
  updatereason?: string;
};

type Args = {
  roleId?: number;
  searchText?: string | null;
};

export const useRightsService = ({ roleId, searchText }: Args = {}) => {
  // 1) Rights matrix (ALL forms+actions)
  const SelectAllQuery = useQuery({
    queryKey: ["rights-selectAll", searchText ?? ""],
    queryFn: () => rightsApiService.getSelectAllData(searchText), // ignore searchText if your API doesn't use it
    enabled: true, // <-- MUST ALWAYS be enabled to render the grid
  });

  // 2) Assigned rights for a specific role
  const SelectAssignedQuery = useQuery({
    queryKey: ["rights-assigned", roleId],
    queryFn: () => rightsApiService.getSelectData(roleId!), // expects roleId
    enabled: !!roleId,
  });

  // 3) Save rights for a role (dedicated payload type)
  const UpdateRightsMutation = useMutation<
    ApiResponse,
    Error,
    UpdateRightsPayload
  >({
    mutationFn: (payload) => rightsApiService.updateData(payload),
  });

  const InsertRightsMutation = useMutation<
    ApiResponse,
    Error,
    InsertRightsPayload
  >({
    mutationFn: (payload) => rightsApiService.insertRoleRights(payload),
  });

  return {
    SelectAllQuery,
    SelectAssignedQuery,
    InsertRightsMutation,
  };

  return {
    SelectAllQuery,
    SelectAssignedQuery,
    UpdateRightsMutation,
    InsertRightsMutation,
    // DeleteMutation,
  };
};
